import type { PortalSession, User } from "@/models/user";
import { API_BASE, type APIResponse, callApi } from "@/services/api";

export interface UserUpdateBody {
  firstName?: string;
  lastName?: string;
  unconfirmedEmail?: string;
  gender?: "male" | "female" | "other";
  userTimezone?: string;
  birthDate?: string;
  currentPassword?: string;
  password?: string;
  passwordConfirmation?: string;
  base64Avatar?: string;
  companyRole?: string;
}

// GET user
export async function getUser(): Promise<APIResponse<User>> {
  try {
    const resp = await callApi<User>(`${API_BASE}/current_user`);
    return resp;
  } catch (e) {
    throw e;
  }
}

// Edit user
export async function editUser(
  opts: UserUpdateBody,
): Promise<APIResponse<User>> {
  try {
    const resp = await callApi<User>(`${API_BASE}/current_user`, {
      method: "PUT",
      body: opts,
    });
    return resp;
  } catch (e) {
    throw e;
  }
}

// Delete user's current avatar
export async function deleteCurrentAvatar(): Promise<void> {
  try {
    await callApi(`${API_BASE}/current_user/avatar`, {
      method: "DELETE",
    });
  } catch (e) {
    throw e;
  }
}

/**
 * Submit an account deletion request for the signed-in user.
 *
 * DELETE /api/v2/current_user responds 200 with an empty body. Deletion is
 * asynchronous: a 2xx means the request was accepted, the account is
 * deactivated, and every session token is revoked server-side — the caller
 * must treat the local session as dead. Data is purged after a 30-day grace
 * period. Refusals carry the reason in `errors`: 403 for staff accounts and
 * group owners, 422 when the subscription cancel or the deactivation fails.
 */
export async function requestAccountDeletion(): Promise<void> {
  await callApi(`${API_BASE}/current_user`, { method: "DELETE" });
}

// Get Stripe portal session link
export async function stripePortal(): Promise<APIResponse<PortalSession>> {
  try {
    const resp = await callApi<PortalSession>(
      `${API_BASE}/current_user/stripe/portal`,
    );
    return resp;
  } catch (e) {
    throw e;
  }
}
